import React, { useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import LoginDrawerTrigger from './LoginDrawerTrigger.jsx';
import DesktopAccountMenu from './DesktopAccountMenu.jsx';
import BrandLogo from './header/BrandLogo.jsx';
import PrimaryNav from './header/PrimaryNav.jsx';
import MobileNav from './header/MobileNav.jsx';

function SiteHeader({
    navItems,
    activeHref,
    isMenuOpen,
    onToggleMenu,
    onCloseMenu,
    desktopAccountMenuProps,
    shouldDisableDrawer,
    logoHref,
}) {
    const mobileMenuButtonRef = useRef(null);
    const mobileMenuId = 'site-mobile-navigation';
    const translate = desktopAccountMenuProps?.t;

    useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key !== 'Escape') {
                return;
            }

            event.preventDefault();
            onCloseMenu?.();
            mobileMenuButtonRef.current?.focus();
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isMenuOpen, onCloseMenu]);

    return (
        <nav
            aria-label={translate?.('nav.primary')}
            className="fixed top-0 w-full z-50 bg-[var(--glass-bg)] backdrop-blur-md border-b border-[var(--glass-border)]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="flex items-center justify-between h-16 gap-3 lg:gap-4">
                    <BrandLogo href={logoHref} />
                    <PrimaryNav items={navItems} activeHref={activeHref} />
                    <div className="flex shrink-0 items-center gap-3">
                        <DesktopAccountMenu {...desktopAccountMenuProps} />
                        {!shouldDisableDrawer && <LoginDrawerTrigger className="xl:hidden" />}
                        <button
                            ref={mobileMenuButtonRef}
                            type="button"
                            onClick={onToggleMenu}
                            aria-controls={mobileMenuId}
                            aria-expanded={isMenuOpen}
                            aria-label={translate?.(isMenuOpen ? 'nav.close_menu' : 'nav.open_menu')}
                            className="flex h-11 w-11 items-center justify-center rounded-md text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 xl:hidden"
                            data-testid="site-header-compact-navigation-trigger"
                        >
                            {isMenuOpen ? (
                                <X className="w-6 h-6" aria-hidden="true" />
                            ) : (
                                <Menu className="w-6 h-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <MobileNav
                id={mobileMenuId}
                items={navItems}
                activeHref={activeHref}
                isOpen={isMenuOpen}
                onNavigate={onCloseMenu}
                accountMenuProps={desktopAccountMenuProps}
            />
        </nav>
    );
}

export default SiteHeader;
